import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/queryClient";
import { getStatusColor } from '../lib/statusColors';
import { 
  Package, 
  FileUp, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Calendar
} from "lucide-react";
import ActivityCard from "@/components/dashboard/ActivityCard";

interface TendererAuth {
  email: string;
  accessCode: string;
  companyName: string;
  authenticatedAt: string;
}

interface TenderPackage {
  id: string;
  name: string;
  packageNumber: string;
  description?: string;
  totalFiles: number;
  createdAt: string;
  submissionDeadline?: string;
  sentAt: string;
  status: string;
  hubName?: string;
  projectName?: string;
  projectId?: string;
  shareToken?: string;
}

interface SubmittedReturn {
  submissionId: string;
  packageId: string;
  packageName: string;
  packageNumber: string;
  description?: string;
  submittedAt: string;
  notes?: string;
  status: string;
  recipientCompany: string;
  hubName?: string;
  projectName?: string;
  filesUploaded?: number;
}

interface TendererDashboardProps {
  authData: TendererAuth;
}

export function TendererDashboard({ authData }: TendererDashboardProps) {
  // Mock selected project for URL generation
  const selectedProject = { id: 'current-project' };
  // Fetch tender packages
  const { data: packages = [], isLoading: packagesLoading, refetch: refetchPackages } = useQuery<TenderPackage[]>({
    queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode],
    queryFn: () => apiRequest('/api/tenderer/packages', {
      method: 'POST',
      body: JSON.stringify({
        email: authData?.email,
        accessCode: authData?.accessCode
      })
    }),
    enabled: !!authData?.email && !!authData?.accessCode,
    refetchInterval: 30000, // Refresh every 30 seconds to catch status updates
  });

  // Fetch submitted returns (packages we've submitted tender responses for)
  const { data: submittedReturns = [], isLoading: returnsLoading } = useQuery<SubmittedReturn[]>({
    queryKey: ['/api/tenderer/submitted-returns', authData?.email, authData?.accessCode],
    queryFn: () => apiRequest(`/api/tenderer/submitted-returns?email=${encodeURIComponent(authData?.email || '')}&accessCode=${encodeURIComponent(authData?.accessCode || '')}`, {
      method: 'GET'
    }),
    enabled: !!authData?.email && !!authData?.accessCode,
    refetchInterval: 30000, // Refresh every 30 seconds to catch status updates
  });

  // Fetch activities data for tenderer
  const { data: activitiesData = [], isLoading: activitiesLoading, refetch: refetchActivities } = useQuery({
    queryKey: ['/api/tenderer/activities', authData?.email, authData?.accessCode],
    queryFn: () => apiRequest(`/api/tenderer/activities?email=${encodeURIComponent(authData?.email || '')}&accessCode=${encodeURIComponent(authData?.accessCode || '')}`, {
      method: 'GET'
    }),
    enabled: !!authData?.email && !!authData?.accessCode,
    staleTime: 5000,
    refetchInterval: 10000,
  });

  const getDeadlineStatus = (deadline?: string) => {
    if (!deadline) return null;
    
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const daysUntil = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) {
      return { status: 'overdue', color: 'text-red-600', bgColor: 'bg-red-50 border-red-200', icon: AlertTriangle };
    } else if (daysUntil <= 7) {
      return { status: 'urgent', color: 'text-orange-600', bgColor: 'bg-orange-50 border-orange-200', icon: Clock };
    } else {
      return { status: 'normal', color: 'text-green-600', bgColor: 'bg-green-50 border-green-200', icon: CheckCircle };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatReceivedDate = (dateString: string) => {
    if (!dateString) return 'Not set';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };


  // Calculate stats
  const totalPackages = packages.length;
  const totalReturns = submittedReturns.length;
  const pendingPackages = packages.filter(pkg => !submittedReturns.some(ret => ret.packageId === pkg.id)).length;
  const urgentPackages = packages.filter(pkg => {
    const deadline = getDeadlineStatus(pkg.submissionDeadline);
    return deadline && (deadline.status === 'urgent' || deadline.status === 'overdue');
  }).length;

  if (packagesLoading || returnsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header - Compact */}
      <div>
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <span className="text-gray-600">Overview of your tender packages and submissions</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Packages */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Packages</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPackages}</div>
            <p className="text-xs text-muted-foreground">
              Available for download
            </p>
          </CardContent>
        </Card>

        {/* Submitted Returns */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Submitted Returns</CardTitle>
            <FileUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalReturns}
              {totalPackages > 0 && (
                <span className="text-sm text-green-600 ml-1">
                  ({Math.round((totalReturns / totalPackages) * 100)}%)
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Tender returns submitted
            </p>
          </CardContent>
        </Card>

        {/* Pending Submissions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Submissions</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {pendingPackages}
              {totalPackages > 0 && (
                <span className="text-sm text-orange-600 ml-1">
                  ({Math.round((pendingPackages / totalPackages) * 100)}%)
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Awaiting your response
            </p>
          </CardContent>
        </Card>

        {/* Urgent Deadlines */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Urgent Deadlines</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {urgentPackages}
              {totalPackages > 0 && (
                <span className="text-sm text-red-600 ml-1">
                  ({Math.round((urgentPackages / totalPackages) * 100)}%)
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Due within 7 days
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Packages */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Recent Packages
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {packages.slice(0, 5).map((pkg) => {
              const isSubmitted = submittedReturns.some(ret => ret.packageId === pkg.id);
              
              return (
                <div 
                  key={pkg.id} 
                  className="p-5 hover:bg-blue-50 cursor-pointer transition-all duration-200 border-l-4 border-transparent hover:border-blue-400 hover:shadow-sm"
                  onClick={() => {
                    // Use the current project from URL or package's project, not fallback
                    const currentLocation = window.location.pathname;
                    const urlMatch = currentLocation.match(/\/([^/]+)\/tenderer\/([^/]+)/);
                    const currentProjectId = urlMatch ? urlMatch[1] : (pkg.projectId || 'b.2ca40a1f-d9f7-499f-9e97-bd3bb4b62e16');
                    const companyCode = authData.accessCode;
                    window.location.href = `/packages/${currentProjectId}/tenderer/${companyCode}`;
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-blue-600" />
                          <span className="font-semibold text-blue-600 text-sm">{pkg.packageNumber}</span>
                        </div>
                        <span className="text-sm text-gray-900 font-medium">{pkg.name}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span className="flex items-center gap-1">
                          <FileUp className="h-3 w-3" />
                          {pkg.totalFiles} files
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Received {formatReceivedDate(pkg.sentAt || pkg.createdAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due {formatReceivedDate(pkg.submissionDeadline || pkg.sentAt)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center ml-4">
                      {isSubmitted || pkg.status === 'tender_returned' ? (
                        <Badge className="bg-green-50 text-green-700 border border-green-200 text-xs px-2 py-1" variant="secondary">
                          Returned
                        </Badge>
                      ) : pkg.status === 'awarded' ? (
                        <Badge className="bg-green-50 text-green-700 border border-green-200 text-xs px-2 py-1" variant="secondary">
                          Awarded
                        </Badge>
                      ) : (
                        <Badge className={`${getStatusColor(pkg.status)} text-xs px-2 py-1`} variant="secondary">
                          {pkg.status === 'sent' ? 'Received' : 
                           pkg.status === 'viewed' ? 'Viewed' :
                           pkg.status === 'downloaded' ? 'Downloaded' :
                           pkg.status === 'tender_returned' ? 'Returned' : 
                           pkg.status === 'awarded' ? 'Awarded' :
                           pkg.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {packages.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No packages available</p>
              </div>
            )}
          </div>
        </div>

        {/* Activity Feed */}
        <ActivityCard 
          activities={activitiesData || []}
          isLoading={activitiesLoading}
          onRefresh={refetchActivities}
        />
      </div>
    </div>
  );
}